import { DestroyRef, inject, Injectable, NgZone, signal } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { catchError, of, Subscription, timeout } from 'rxjs';
import { OrdersCurrentService } from '@callcenter-app/service/orders-current.service';

/** «Ваши за сегодня» of the shift card: the operator's own day, live (`GET …/my/today`, the operator is taken from the token). */
export interface MyTodayModel {
  /** The day the figures are for (`2026-10-20`), in the time zone of the operator's country. */
  date: string;
  answeredCalls: number;
  /** Orders the operator created today, without the cancelled and the refunded (any currency). */
  createdCount: number;
  /** Their sum in {@link currency} — the currency of the operator's country; orders in another currency are not in it. */
  createdRevenue: number;
  currency: string;
}

export const MY_TODAY_URL = '/api/vn/reports/calls/monitoring/my/today';
/** The badge «опаздывают» of «Заказы» — the tile of the page without filters. */
export const LATE_POLL_MS = 60_000;
export const TODAY_POLL_MS = 120_000;
/** A request that hangs is given up: the next one is asked in its period (an answer in flight holds the next one back). */
export const POLL_TIMEOUT_MS = 30_000;

/**
 * The two figures the new menu shows on every page (the owner's word «делаем все», 20.10): the late orders at «Заказы» and «ваши за сегодня» of the shift card. Each is asked at once and then every period, only while the tab
 * is visible (a hidden tab asks nothing; coming back asks at once). A failed answer keeps the last figure — the menu never blinks to zero. The timers run outside the zone; a new figure is a signal.
 */
@Injectable({ providedIn: 'root' })
export class ShellPollsService {
  readonly late = signal<number | undefined>(undefined);
  readonly today = signal<MyTodayModel | undefined>(undefined);

  private readonly orders = inject(OrdersCurrentService);
  private readonly http = inject(HttpClient);
  private readonly zone = inject(NgZone);
  private readonly doc = inject(DOCUMENT);
  private started = false;
  private stop?: () => void;

  constructor() {
    inject(DestroyRef).onDestroy(() => this.stop?.());
  }

  /** Idempotent: the menu calls it once it is on the screen. */
  start(): void {
    if (this.started) {
      return;
    }
    this.started = true;
    const late = this.every(LATE_POLL_MS, () => this.orders.getSummary({}).pipe(timeout(POLL_TIMEOUT_MS), catchError(() => of(undefined))).subscribe(s => {
      if (s?.tiles) {
        this.late.set(s.tiles.late);
      }
    }));
    const today = this.every(TODAY_POLL_MS, () => this.http.get<MyTodayModel>(MY_TODAY_URL).pipe(timeout(POLL_TIMEOUT_MS), catchError(() => of(undefined))).subscribe(t => {
      if (t) {
        this.today.set(t);
      }
    }));
    this.stop = () => {
      late();
      today();
      this.started = false;
    };
  }

  /** Runs `ask` now (if visible), then every `periodMs` while visible, and at once when the tab becomes visible again after a missed period. */
  private every(periodMs: number, ask: () => Subscription): () => void {
    let last = 0;
    let inFlight: Subscription | undefined;
    const tick = () => {
      if (this.doc.visibilityState === 'hidden' || (inFlight && !inFlight.closed)) {
        return;
      }
      if (Date.now() - last < periodMs - 500) {
        return;
      }
      last = Date.now();
      this.zone.run(() => inFlight = ask());
    };
    const onVisible = () => tick();
    let timer: ReturnType<typeof setInterval> | undefined;
    this.zone.runOutsideAngular(() => {
      timer = setInterval(tick, Math.min(periodMs, 15_000));
      this.doc.addEventListener('visibilitychange', onVisible);
    });
    tick();
    return () => {
      clearInterval(timer);
      this.doc.removeEventListener('visibilitychange', onVisible);
      inFlight?.unsubscribe();
    };
  }
}
